import { useEffect, useRef, useState } from 'react';
import { ArrowRight, Wifi, CreditCard, Users, Store } from 'lucide-react';

const ICON_URL = 'https://customer-assets.emergentagent.com/job_pay-next-gen/artifacts/1oy2wqev_ICON.png';

const advantages = [
  {
    number: '01',
    icon: CreditCard,
    title: 'Replace Checks & Slow Disbursements',
    description: 'Payroll, insurance, government, gig, healthcare, and education payouts are high-friction, high-pain use cases. NexGenPay converts every slow, expensive disbursement into an instant digital credit — with full audit trails and bank-controlled delivery.',
    metric: '$3.5T+',
    metricLabel: 'Annual B2C payments still running slow',
    tags: ['Payroll', 'Insurance', 'Gov Disbursements', 'Gig Economy', 'Healthcare'],
    color: '#00FF00',
  },
  {
    number: '02',
    icon: Wifi,
    title: 'Turn Instant Payments From a Cost Into Revenue',
    description: "Zelle is a retention utility — your bank processes billions and earns $0. NexGenPay is a monetizable bank-controlled layer. FedNow's 2026 credit transfer fee is $0.045 per item, with the first 2,500 monthly transfers effectively discounted. That's materially different economics from any card or network structure.",
    metric: '$0.045',
    metricLabel: 'FedNow per-item fee — vs. zero Zelle revenue',
    tags: ['FedNow Rails', 'Sub-1% Fees', 'Bank-Owned Revenue', 'Non-Interest Income'],
    color: '#00FF00',
  },
  {
    number: '03',
    icon: Users,
    title: 'Win the Underbanked & Newly Banked On-Ramp',
    description: "FDIC data: 4.2% of U.S. households were unbanked and 14.2% were underbanked in 2023. NexGenPay's wallet-first, SMS-triggered onboarding with cardless cash access starts before a full DDA is opened. That's a real acquisition engine — and no other bank-owned product reaches this market this way.",
    metric: '18.4%',
    metricLabel: 'Of U.S. households unbanked or underbanked (FDIC 2023)',
    tags: ['SMS Onboarding', 'Cardless Cash', 'Pre-DDA Wallet', 'Financial Inclusion'],
    color: '#00FF00',
  },
  {
    number: '04',
    icon: Store,
    title: 'Merchant Loop & Bank-Owned Acceptance',
    description: 'QR-pay closes the loop: wallet utility becomes merchant acceptance, instant settlement, sub-1% fees, zero chargebacks, and bank-owned transaction data. This is where the model shifts from infrastructure to a defensible margin business — and where strategic value compounds over time.',
    metric: '<1%',
    metricLabel: 'Merchant fee — with 0 chargebacks & instant settlement',
    tags: ['QR Pay', 'Merchant Acceptance', 'Instant Settlement', 'Bank Data Ownership'],
    color: '#00FF00',
  },
];

export default function AdvantageSection() {
  const sectionRef = useRef(null);
  const [revealed, setRevealed] = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);

  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setRevealed(true); obs.disconnect(); } },
      { threshold: 0.08 }
    );
    if (sectionRef.current) obs.observe(sectionRef.current);
    return () => obs.disconnect();
  }, []);

  return (
    <section
      id="advantage"
      data-testid="advantage-section"
      ref={sectionRef}
      className="relative section-padding bg-black"
      style={{ scrollMarginTop: '80px' }}
    >
      {/* Background accent */}
      <div className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage: 'linear-gradient(rgba(0,255,0,0.02) 1px, transparent 1px), linear-gradient(90deg, rgba(0,255,0,0.02) 1px, transparent 1px)',
          backgroundSize: '80px 80px',
        }} />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] pointer-events-none"
        style={{ background: 'radial-gradient(ellipse, rgba(0,255,0,0.04), transparent 70%)' }} />

      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-16 items-end"
          style={{ opacity: revealed ? 1 : 0, transform: revealed ? 'translateY(0)' : 'translateY(30px)', transition: 'all 0.7s ease' }}>
          <div>
            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-[2px] bg-[#00FF00]" />
              <span className="text-[#00FF00] font-mono text-[11px] uppercase tracking-[0.4em]">Competitive Advantage</span>
            </div>
            <h2 className="text-4xl sm:text-5xl font-black uppercase text-white leading-none"
              style={{ fontFamily: 'Unbounded, sans-serif', letterSpacing: '-0.02em' }}>
              Where NexGenPay
              <br /><span style={{ color: '#00FF00', textShadow: '0 0 30px rgba(0,255,0,0.3)' }}>Has Real Edge</span>
            </h2>
          </div>
          <p className="text-[#A3A3A3] text-base leading-relaxed" style={{ fontFamily: 'Manrope, sans-serif' }}>
            The strongest commercial wedge isn't "be everything on day one." It's owning four high-value entry points where incumbent banks are structurally vulnerable and NexGenPay has a decisive, first-mover advantage.
          </p>
        </div>

        {/* Desktop: Side-by-side selector */}
        <div className="hidden lg:grid grid-cols-5 gap-0 border border-[#1A1A1A]"
          style={{ opacity: revealed ? 1 : 0, transition: 'opacity 0.7s ease 0.2s' }}>

          {/* Left: Tab selector */}
          <div className="col-span-2 border-r border-[#1A1A1A]">
            {advantages.map((a, i) => {
              const Icon = a.icon;
              const isActive = activeIdx === i;
              return (
                <button
                  key={a.number}
                  onClick={() => setActiveIdx(i)}
                  data-testid={`advantage-tab-${i}`}
                  className="w-full text-left p-6 border-b border-[#1A1A1A] last:border-b-0 transition-all duration-300 group"
                  style={{
                    background: isActive ? 'rgba(0,255,0,0.05)' : 'transparent',
                    borderLeft: isActive ? '3px solid #00FF00' : '3px solid transparent',
                  }}
                >
                  <div className="flex items-start gap-4">
                    <span className="font-mono text-2xl font-black opacity-20 flex-shrink-0"
                      style={{ color: isActive ? '#00FF00' : '#fff', opacity: isActive ? 0.6 : 0.15, fontFamily: 'JetBrains Mono, monospace' }}>
                      {a.number}
                    </span>
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <Icon size={14} color={isActive ? '#00FF00' : '#A3A3A3'} />
                        <span className={`font-bold text-xs uppercase tracking-wide ${isActive ? 'text-white' : 'text-[#A3A3A3]'}`}
                          style={{ fontFamily: 'Unbounded, sans-serif', fontSize: '11px' }}>
                          {a.title}
                        </span>
                      </div>
                      <div className="font-mono text-xs" style={{ color: isActive ? '#00FF00' : '#555' }}>
                        {a.metric}
                      </div>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>

          {/* Right: Content panel */}
          <div className="col-span-3 p-10 flex flex-col justify-between" style={{ background: '#0A0A0A' }}>
            {advantages.map((a, i) => {
              const Icon = a.icon;
              if (i !== activeIdx) return null;
              return (
                <div key={a.number} style={{ animation: 'fadeIn 0.4s ease' }}>
                  <div className="flex items-center gap-3 mb-2">
                    <span className="font-mono text-5xl font-black text-[#00FF00] opacity-20"
                      style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                      {a.number}
                    </span>
                  </div>
                  <div className="flex items-center gap-3 mb-4">
                    <div className="p-2 border border-[#00FF00] border-opacity-30"
                      style={{ background: 'rgba(0,255,0,0.08)' }}>
                      <Icon size={20} color="#00FF00" />
                    </div>
                    <h3 className="text-white font-black text-base uppercase leading-tight"
                      style={{ fontFamily: 'Unbounded, sans-serif', fontSize: '14px' }}>
                      {a.title}
                    </h3>
                  </div>
                  <p className="text-[#A3A3A3] text-sm leading-relaxed mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    {a.description}
                  </p>

                  {/* Metric highlight */}
                  <div className="border border-[#00FF00] border-opacity-20 p-4 mb-6"
                    style={{ background: 'rgba(0,255,0,0.04)' }}>
                    <div className="flex items-center gap-4">
                      <div>
                        <div className="font-mono text-3xl font-black text-[#00FF00]"
                          style={{ fontFamily: 'JetBrains Mono, monospace', textShadow: '0 0 20px rgba(0,255,0,0.4)' }}>
                          {a.metric}
                        </div>
                        <div className="text-[#A3A3A3] text-xs mt-1" style={{ fontFamily: 'Manrope, sans-serif' }}>
                          {a.metricLabel}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Tags */}
                  <div className="flex flex-wrap gap-2 mb-8">
                    {a.tags.map(tag => (
                      <span key={tag} className="border border-[#1A1A1A] px-3 py-1 font-mono text-[10px] text-[#00FF00] uppercase tracking-wider"
                        style={{ background: 'rgba(0,255,0,0.05)' }}>
                        {tag}
                      </span>
                    ))}
                  </div>

                  <button
                    onClick={() => document.querySelector('#demo')?.scrollIntoView({ behavior: 'smooth' })}
                    data-testid={`advantage-cta-${i}`}
                    className="btn-neon text-xs flex items-center gap-2"
                    style={{ fontFamily: 'Unbounded, sans-serif', padding: '12px 24px' }}
                  >
                    Activate This for Your Bank <ArrowRight size={14} />
                  </button>
                </div>
              );
            })}
          </div>
        </div>

        {/* Mobile: Stacked cards */}
        <div className="lg:hidden flex flex-col gap-4">
          {advantages.map((a, i) => {
            const Icon = a.icon;
            return (
              <div
                key={a.number}
                data-testid={`advantage-card-mobile-${i}`}
                className="border border-[#1A1A1A] p-6"
                style={{
                  background: '#0A0A0A',
                  opacity: revealed ? 1 : 0,
                  transform: revealed ? 'translateY(0)' : 'translateY(30px)',
                  transition: `all 0.6s ease ${0.1 + i * 0.1}s`,
                }}
              >
                <div className="flex items-center gap-3 mb-4">
                  <span className="font-mono text-3xl font-black text-[#00FF00] opacity-30"
                    style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                    {a.number}
                  </span>
                  <div>
                    <div className="flex items-center gap-2">
                      <Icon size={14} color="#00FF00" />
                      <span className="text-white font-bold text-xs uppercase tracking-wide"
                        style={{ fontFamily: 'Unbounded, sans-serif', fontSize: '11px' }}>
                        {a.title}
                      </span>
                    </div>
                    <div className="font-mono text-lg font-black text-[#00FF00]"
                      style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                      {a.metric}
                    </div>
                  </div>
                </div>
                <p className="text-[#A3A3A3] text-sm leading-relaxed" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  {a.description}
                </p>
              </div>
            );
          })}
        </div>
      </div>

      <style>{`@keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }`}</style>
    </section>
  );
}
